import classNames from 'classnames';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfISOWeek,
  format,
  isAfter,
  startOfISOWeek,
} from 'date-fns';
import { FC, memo, useMemo, useRef, useState } from 'react';
import { CalendarProps } from 'react-calendar';

import { ReactComponent as ArrowIcon } from 'assets/images/icons/arrow-left.svg';

import { ButtonSquare } from 'ui/Button/ButtonSquare';
import Calendar from 'ui/Calendar';

import useClickOutside from 'hooks/useClickOutside';

import { DateTypes } from 'entities/view/sensorDetails';
import { Value } from 'react-calendar/dist/shared/types';

type PropTypes = Omit<CalendarProps, 'onChange'> & {
  type: DateTypes;
  date: Date | [Date, Date];
  onChange: (date: Date | [Date, Date]) => void;
};

const FORMAT = 'dd.MM.yyyy';
const MONTH_FORMAT = 'MMMM yyyy';

const DateChanger: FC<PropTypes> = ({ date, onChange, type, ...props }) => {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);

  const isDateArray = useMemo(() => Array.isArray(date), [date]);
  const renderDate = useMemo(
    () =>
      isDateArray
        ? `${format((date as [Date, Date])?.[0], FORMAT)} - ${format((date as [Date, Date])?.[1], FORMAT)}`
        : date
          ? type === DateTypes.DAY
            ? format(date as Date, FORMAT)
            : format(date as Date, MONTH_FORMAT)
          : '',
    [date, isDateArray, type],
  );

  const handleLeftClick = () => {
    changeDate({ date, type, onChange, direction: 'prev' });
  };

  const handleRightClick = () => {
    changeDate({ date, type, onChange, direction: 'next' });
  };

  const handleClose = () => setOpen(false);
  const handleToogle = () => setOpen((prevState) => !prevState);

  const getDisabledTile = ({ date }: { date: Date }) => isAfter(date, new Date());

  const handleCalendarChange = (value: Value) => {
    let returnedValue = value;

    if (type === DateTypes.WEEK) {
      returnedValue = [
        startOfISOWeek((value as [Date, Date])?.[0]),
        endOfISOWeek((value as [Date, Date])?.[0]),
      ];
    }

    onChange(returnedValue as Date);
  };

  useClickOutside(ref, handleClose);

  return (
    <div ref={ref} className="date-changer">
      <ButtonSquare variant="secondary" onClick={handleLeftClick} className="date-changer__button">
        <ArrowIcon />
      </ButtonSquare>

      <span className="date-changer__title" onClick={handleToogle}>
        {renderDate}
      </span>

      <ButtonSquare
        variant="secondary"
        onClick={handleRightClick}
        className={classNames('date-changer__button', 'date-changer__right')}
        disabled={isDisabledNextDate(type, date)}
      >
        <ArrowIcon />
      </ButtonSquare>

      {isOpen && (
        <div className="date-changer__dropdown">
          <Calendar
            value={date}
            selectRange={false}
            tileDisabled={getDisabledTile}
            onChange={handleCalendarChange}
            isRange={type === DateTypes.WEEK}
            onClickMonth={type === DateTypes.MONTH ? handleCalendarChange : undefined}
            view={type === DateTypes.MONTH ? 'year' : undefined}
            {...props}
          />
        </div>
      )}
    </div>
  );
};

function changeDate({
  date,
  type,
  onChange,
  direction,
}: {
  type?: DateTypes;
  date: Date | [Date, Date];
  direction?: 'prev' | 'next';
  onChange: (date: Date | [Date, Date]) => void;
}) {
  const value = direction === 'next' ? 1 : -1;

  if (type === DateTypes.DAY) {
    onChange(addDays(date as Date, value));
  } else if (type === DateTypes.WEEK) {
    onChange([addWeeks((date as Date[])?.[0], value), addWeeks((date as Date[])?.[1], value)]);
  } else if (type === DateTypes.MONTH) {
    onChange(addMonths(date as Date, value));
  }
}

function isDisabledNextDate(type: DateTypes, date: Date | [Date, Date]) {
  const next: Date =
    type === DateTypes.DAY
      ? addDays(date as Date, 1)
      : type === DateTypes.WEEK
        ? addWeeks((date as [Date, Date])[0], 1)
        : addMonths(date as Date, 1);

  const today = new Date();

  return isAfter(next, today);
}

export default memo(DateChanger);
